import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TicketType } from '../types';
import { transactionService, ticketTypeService } from '../services/api';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [loadingTicketTypes, setLoadingTicketTypes] = useState(true);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccessMessage('Payment completed successfully! Your tickets have been generated.');
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchTicketTypes = async () => {
      try {
        const ticketTypes = await ticketTypeService.getTicketTypes();
        setTicketTypes(ticketTypes);
      } catch (err) {
        setError('Failed to load ticket types');
        console.error('Error fetching ticket types:', err);
      } finally {
        setLoadingTicketTypes(false);
      }
    };

    fetchTicketTypes();
  }, []);

  const handleTicketSelection = useCallback(async (ticketType: TicketType) => {
    setIsLoading(true);
    setError(null);

    try {
      const transactionData = await transactionService.initiate(ticketType);
      console.log('[HomePage] Transaction initiated:', transactionData);
      console.log('[HomePage] Payment method:', transactionData.payment_method);
      console.log('[HomePage] Payment data:', transactionData.payment_data);

      // Route based on payment method configured for this kiosk
      if (transactionData.payment_method === 'viva') {
        if (transactionData.ask_email) {
          // NFC Flow with email collection first
          console.log('[HomePage] Routing to email input page (Viva NFC with ask_email)');
          navigate('/email', {
            state: {
              transactionId: transactionData.transaction_id,
              ticketType: ticketType,
              amount: transactionData.amount,
              paymentMethod: 'viva',
              sessionToken: transactionData.payment_data?.session_token,
              testingMode: transactionData.testing_mode,
            }
          });
        } else {
          // NFC Flow - Viva Intent (no email)
          console.log('[HomePage] Routing to NFC payment flow (Viva)');
          console.log('[HomePage] Session token:', transactionData.payment_data?.session_token ? 'present' : 'missing');
          navigate('/payment/nfc', {
            state: {
              transactionId: transactionData.transaction_id,
              ticketType: ticketType,
              amount: transactionData.amount,
              sessionToken: transactionData.payment_data?.session_token,
            }
          });
        }
      } else {
        // QR Flow - Payconiq (default): collect email first
        console.log('[HomePage] Routing to email input page (Payconiq)');
        console.log('[HomePage] Testing mode:', transactionData.testing_mode);
        navigate('/email', {
          state: {
            transactionId: transactionData.transaction_id,
            ticketType: ticketType,
            amount: transactionData.amount,
            paymentData: transactionData.payment_data,
            testingMode: transactionData.testing_mode,
          }
        });
      }
    } catch (err: any) {
      console.error('Ticket selection error:', err);
      const errorMessage = err.response?.data?.message || 'An error occurred while processing your request';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  if (loadingTicketTypes) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent"></div>
          </div>
          <p className="text-white text-lg font-medium">Loading ticket options...</p>
          <p className="text-gray-400 text-sm mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex flex-col">
      <div className="bg-black/20 backdrop-blur-md border-b border-white/10 py-3 px-6">
        <div className="max-w-6xl mx-auto flex justify-start items-center">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <div className="text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                TICKET MACHINE
              </h1>
              <p className="text-gray-400 text-xs md:text-sm mt-0.5">Select your ticket</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center">
        <div className="max-w-6xl mx-auto px-6 py-8 w-full">
        {successMessage && (
          <div className="mb-8">
            <div className="bg-green-500/20 backdrop-blur-md border border-green-500/30 text-green-100 px-8 py-6 rounded-2xl text-center shadow-lg">
              <div className="text-xl font-bold flex items-center justify-center space-x-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>SUCCESS</span>
              </div>
              <div className="text-sm mt-2 opacity-90">{successMessage}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-8">
            <div className="bg-red-500/20 backdrop-blur-md border border-red-500/30 text-red-100 px-8 py-6 rounded-2xl text-center shadow-lg">
              <div className="text-xl font-bold flex items-center justify-center space-x-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>ERROR</span>
              </div>
              <div className="text-sm mt-2 opacity-90">{error}</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-4 md:gap-6">
          {ticketTypes.map((ticketType) => {
            const basePrice = parseFloat(String(ticketType.price));
            const feePercentage = parseFloat(String(ticketType.fee_percentage || 0));
            const feeAmount = (basePrice * feePercentage) / 100;
            const totalPrice = basePrice + feeAmount;

            return (
            <div
              key={ticketType.id}
              className="group bg-white/10 backdrop-blur-md border border-white/20 hover:border-blue-400/50 rounded-3xl transition-all duration-500 cursor-pointer overflow-hidden min-h-[280px] w-64 flex flex-col shadow-xl hover:shadow-2xl hover:scale-105"
              onClick={() => !isLoading && handleTicketSelection(ticketType)}
            >
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-center flex-1 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <div className="relative z-10">
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2 drop-shadow-lg">
                    €{basePrice.toFixed(2)}
                  </div>
                  {feeAmount > 0 && (
                    <div className="text-white/70 text-xs mb-2">
                      Base: €{basePrice.toFixed(2)} + Fee: €{feeAmount.toFixed(2)}
                    </div>
                  )}
                  <div className="text-white/90 text-sm font-medium tracking-wide">
                    Virtual Ticket
                  </div>
                  {ticketType.description && (
                    <div className="text-white/70 text-xs mt-2 px-2 line-clamp-2">
                      {ticketType.description}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-black/20 backdrop-blur-sm p-6 text-center border-t border-white/10">
                <div className="text-white text-base font-medium flex items-center justify-center space-x-2">
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>PROCESSING...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                      <span>TAP TO SELECT</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        </div>
      </div>

      <div className="text-center p-3">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-2 max-w-lg mx-auto">
          <div className="text-gray-300 text-xs">
            <div className="mb-1 text-sm font-semibold text-white">
              TICKET MACHINE v2.0
            </div>

            <div className="flex justify-center items-center space-x-3 mb-1">
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secure</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Protected</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>QR Validated</span>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              <div>Secure payment processing • QR code validation</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
